import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface FontSizeSelectorProps {
  className?: string;
  compact?: boolean;
}

const FontSizeSelector: React.FC<FontSizeSelectorProps> = ({ className, compact = false }) => {
  const { theme, setFontSize, getFontSize } = useTheme();
  const currentFontSize = getFontSize();

  const fontSizes = [
    { key: 'small', label: 'A', size: '14px' },
    { key: 'medium', label: 'A', size: '16px' },
    { key: 'large', label: 'A', size: '18px' },
  ] as const;

  const handleFontSizeChange = (size: 'small' | 'medium' | 'large') => {
    setFontSize(size);
  };

  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: theme.borderRadius.md,
        padding: '2px',
        border: `1px solid rgba(255, 255, 255, 0.2)`,
      }} className={className}>
        {fontSizes.map((fontSize) => (
          <button
            key={fontSize.key}
            onClick={() => handleFontSizeChange(fontSize.key)}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: theme.borderRadius.sm,
              border: 'none',
              backgroundColor: currentFontSize === fontSize.key 
                ? 'rgba(255, 255, 255, 0.3)' 
                : 'transparent',
              color: theme.colors.primaryText,
              fontSize: fontSize.size,
              fontWeight: '600',
              fontFamily: theme.fonts.display,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              minWidth: '24px',
            }}
            onMouseEnter={(e) => {
              if (currentFontSize !== fontSize.key) {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }
            }}
            onMouseLeave={(e) => {
              if (currentFontSize !== fontSize.key) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            aria-label={`Set text size to ${fontSize.key}`}
            title={`${fontSize.key.charAt(0).toUpperCase() + fontSize.key.slice(1)} text size`}
          >
            {fontSize.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 0',
    }} className={className}>
      <span style={{
        fontSize: theme.typography.body.fontSize,
        fontWeight: theme.typography.body.fontWeight,
        fontFamily: theme.fonts.secondary,
        color: theme.colors.textSecondary,
        marginRight: '8px',
      }}>
        Text Size:
      </span>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: '4px',
        border: `1px solid ${theme.colors.border}`,
      }}>
        {fontSizes.map((fontSize) => (
          <button
            key={fontSize.key}
            onClick={() => handleFontSizeChange(fontSize.key)}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: theme.borderRadius.sm,
              border: 'none',
              backgroundColor: currentFontSize === fontSize.key 
                ? theme.colors.primary 
                : 'transparent',
              color: currentFontSize === fontSize.key 
                ? theme.colors.primaryText 
                : theme.colors.text,
              fontSize: fontSize.size,
              fontWeight: '600',
              fontFamily: theme.fonts.display,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              minWidth: '32px',
            }}
            onMouseEnter={(e) => {
              if (currentFontSize !== fontSize.key) {
                e.currentTarget.style.backgroundColor = theme.colors.border;
              }
            }}
            onMouseLeave={(e) => {
              if (currentFontSize !== fontSize.key) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            aria-label={`Set text size to ${fontSize.key}`}
            title={`${fontSize.key.charAt(0).toUpperCase() + fontSize.key.slice(1)} text size`}
          >
            {fontSize.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FontSizeSelector; 